import { Link, useLocation } from 'react-router-dom'
import { useCommonT } from '../../i18n'
import { useMobileNavSuppressed } from '../../lib/mobileNavSuppression'
import { navV2Enabled } from '../../lib/navV2'
import MobileNavV2 from './MobileNavV2'

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
    <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625A1.875 1.875 0 013.75 19.875v-6.198a2.29 2.29 0 01.091-.086L12 5.432z" />
  </svg>
)

const BracketIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 01-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 013 19.875v-6.75z" />
  </svg>
)

const BuildIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M12 6.75a5.25 5.25 0 016.775-5.025.75.75 0 01.313 1.248l-3.32 3.319c.063.475.276.934.641 1.299.365.365.824.578 1.3.64l3.318-3.319a.75.75 0 011.248.313 5.25 5.25 0 01-5.472 6.756c-1.018-.086-1.87.1-2.309.634L7.344 21.3A3.298 3.298 0 112.7 16.657l8.684-7.151c.533-.44.72-1.291.634-2.309A5.342 5.342 0 0112 6.75z" clipRule="evenodd" />
  </svg>
)

const RulesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
  </svg>
)

const CollectionIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375z" />
    <path fillRule="evenodd" d="M3.087 9l.54 9.176A3 3 0 006.62 21h10.757a3 3 0 002.995-2.824L20.913 9H3.087zm6.163 3.75A.75.75 0 0110 12h4a.75.75 0 010 1.5h-4a.75.75 0 01-.75-.75z" clipRule="evenodd" />
  </svg>
)

// The five product tabs. `id` keys the label (common:nav.<id>).
const TABS = [
  { path: '/', id: 'home', Icon: HomeIcon },
  { path: '/bracket', id: 'bracket', Icon: BracketIcon },
  { path: '/build', id: 'build', Icon: BuildIcon },
  { path: '/decks', id: 'my_decks', Icon: CollectionIcon },
  { path: '/rules', id: 'judge', Icon: RulesIcon },
] as const

// Navigation v2 (src/lib/navV2.ts) swaps in MobileNavV2; flag off renders
// MobileNavV1 below, unchanged.
export default function MobileNav() {
  return navV2Enabled() ? <MobileNavV2 /> : <MobileNavV1 />
}

function MobileNavV1() {
  const location = useLocation()
  const t = useCommonT()
  // A page with its own pinned phone bar (the saved deck page) takes this
  // slot while that bar is mounted; see src/lib/mobileNavSuppression.ts.
  const suppressed = useMobileNavSuppressed()
  if (suppressed) return null

  return (
    <nav
      // bottom-[var(--ad-inset-bottom)]: above the Mediavine adhesion ad when
      // one is present below md (phones get it on every app route since
      // 2026-10-08, plus a narrow tablet or desktop window). The ad stays in
      // front and is never moved by us, so the nav moves instead. 0px with no
      // ad (src/lib/adInset.ts). In the className, not an inline style, so
      // src/__tests__/overlaysAboveAd.test.ts can see it.
      className="md:hidden fixed bottom-[var(--ad-inset-bottom,0px)] left-0 right-0 z-50 border-t border-gray-800"
      style={{
        backgroundColor: '#111827',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="flex items-center justify-around h-14">
        {TABS.map(({ path, id, Icon }) => (
          <Link
            key={path}
            to={path}
            className={`flex flex-col items-center gap-0.5 px-4 py-1 text-xs transition-colors ${
              location.pathname === path
                ? 'text-white'
                : 'text-gray-400'
            }`}
          >
            <Icon />
            {t(`nav.${id}`)}
          </Link>
        ))}
      </div>
    </nav>
  )
}
